import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import {errorLog, toPriceStr} from '@/utils';
import globalStore from '@/services/global.state';
import {ProxyH5Header} from '@/common-pages/proxy/proxy-h5.shared';
import {UserListItem} from '../invitation.type';
import {defaultTableData} from '../invitation.variables';

const tableHeaderClassName =
  'flex h-[2.4375rem] items-center bg-[#FF6265] px-[0.8125rem] text-[0.8125rem] font-bold text-white';

const tableCellBaseClassName = 'text-[0.8125rem] font-bold text-[#31373D]';

const rulesContainerClassName =
  'rule-box mt-3 rounded-lg border border-[var(--bg-color-main)] bg-white px-3 pb-7';

export default function InvitationRuleWeb() {
  const {t} = useTranslation();
  const [userList, setUserList] = useState<UserListItem[]>();

  useEffect(() => {
    globalStore.asyncGetItem('invitationBonusList').then(user => {
      try {
        user && setUserList(JSON.parse(user) as UserListItem[]);
      } catch (error) {
        errorLog('error', error);
      }
    });
  }, []);

  const tableRows = useMemo(() => {
    if (!userList?.length) {
      return defaultTableData.map(([invite, deposit, bonus]) => ({
        invite: `${invite} ${t('invitation.rule.table.people')}`,
        deposit: toPriceStr(deposit, {fixed: 0, thousands: true}),
        bonus: toPriceStr(bonus, {fixed: 0, thousands: true}),
      }));
    }

    return userList.map(item => ({
      invite: `${item.inviteNumber} ${t('invitation.rule.table.people')}`,
      deposit: toPriceStr(item.depositAmount, {fixed: 0, thousands: true}),
      bonus: toPriceStr(item.bonusAmount, {fixed: 0, thousands: true}),
    }));
  }, [t, userList]);

  const rules = useMemo(
    () =>
      Array.from({length: 4}, (_, index) =>
        t(`invitation.rule.detail.paragraph${index + 1}`),
      ),
    [t],
  );

  return (
    <div className="proxy-h5-page flex min-h-full flex-col bg-[#EFF2F7]">
      <ProxyH5Header
        title={t('invitation.rule.title')}
        className="h-[3.25rem] border-b border-[rgba(31,35,41,0.06)] shadow-none"
        titleClassName="pt-[0.03125rem] text-[1rem] font-bold leading-none text-[#1F2329]"
        leftClassName="pl-[0.53125rem]"
        rightClassName="pr-[0.65625rem]"
        backButtonClassName="mr-0 h-[1.625rem] w-[1.625rem]"
      />
      <div className="flex-1 overflow-y-auto">
        <div className="px-3 pb-[1.5rem] pt-[0.875rem]">
          <section className="rounded-lg bg-white p-3 text-main text-sm font-medium">
            <p className="leading-[1.45]">
              {t('invitation.rule.paragraph.invite')}
            </p>
            <p className="mt-3 leading-[1.45]">
              {t('invitation.rule.paragraph.after')}
            </p>
          </section>

          <section className="mt-[0.6875rem] overflow-hidden rounded-[0.5rem]">
            <div className={tableHeaderClassName}>
              <div className="w-[7.0625rem] text-left">
                {t('invitation.rule.table.invite')}
              </div>
              <div className="flex-1 text-center">
                {t('invitation.rule.table.deposit')}
              </div>
              <div className="w-[6rem] text-right">
                {t('invitation.rule.table.bonus')}
              </div>
            </div>
            <div className="overflow-hidden rounded-b-[0.5rem]">
              {tableRows.map((row, index) => (
                <div
                  key={`${row.invite}-${index}`}
                  className={clsx(
                    'flex h-10 items-center px-[0.8125rem]',
                    index % 2 === 0 ? 'bg-[#FFD5D5]' : 'bg-[#FFE6E8]',
                  )}>
                  <div
                    className={`w-[7.0625rem] text-left ${tableCellBaseClassName}`}>
                    {row.invite}
                  </div>
                  <div
                    className={`flex-1 text-center ${tableCellBaseClassName}`}>
                    {row.deposit}
                  </div>
                  <div
                    className={`w-[6rem] text-right ${tableCellBaseClassName}`}>
                    {row.bonus}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={rulesContainerClassName}>
            <div className="relative flex justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 218 32"
                className="h-8 w-[13.625rem]">
                <path
                  fill="url(#invitation-rule-header)"
                  d="M0 0h218l-6.924 4.451a24.37 24.37 0 0 0-9.839 12.494A22.42 22.42 0 0 1 180.06 32H37.94a22.42 22.42 0 0 1-21.177-15.055A24.37 24.37 0 0 0 6.924 4.45z"
                />
                <defs>
                  <linearGradient
                    id="invitation-rule-header"
                    x1="179.071"
                    x2="179.071"
                    y1="32"
                    y2="0"
                    gradientUnits="userSpaceOnUse">
                    <stop stopColor="#DB0000" />
                    <stop offset="1" stopColor="#AD0000" />
                  </linearGradient>
                </defs>
              </svg>
              <span className="absolute h-5 w-11 text-base font-bold text-white">
                {t('invitation.rule.detail.title')}
              </span>
            </div>

            <div className="mt-4">
              {rules.map((rule, index) => (
                <div
                  key={index}
                  className={clsx('flex', index === 0 ? '' : 'mt-3')}>
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 8 8"
                      className="h-2 w-2">
                      <path
                        fill="var(--bg-color-main)"
                        d="M3.057 7.057c.52.52 1.365.52 1.886 0l2.114-2.114c.52-.52.52-1.365 0-1.886L4.943.943a1.333 1.333 0 0 0-1.886 0L.943 3.057c-.52.52-.52 1.365 0 1.886z"
                      />
                    </svg>
                  </div>
                  <div className={index === 0 ? 'ml-1' : 'ml-2'}>
                    <span className="text-main text-sm font-medium">
                      {rule}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
